import { listEsims } from "@/api/admin";
import { useAsync } from "@/lib/useAsync";
import { when } from "@/lib/format";
import { EsimLink, OrderLink } from "@/components/EntityLink";
import { SearchBox, useUrlSearch } from "@/components/SearchBox";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import { PagedList, usePagedCursor } from "@/components/ui/paged-list";
import { TD, TH, TR } from "@/components/ui/table";

/**
 * The lines this shop has sold.
 *
 * THE NUMBER IS THE PRODUCT. This shop sells an eSIM that comes with a real
 * +44 number, and the supplier assigns that number some time after the profile
 * is created -- so "no number yet" is a real state with a cron job behind it,
 * and it is the first thing an operator looks for when a customer writes in.
 *
 * ONE SEARCH BOX, because a customer writing in quotes whichever they have:
 * the address they paid from, the number, the order code or the ICCID. The
 * worker reads which one it is.
 */
export function EsimsScreen() {
  const search = useUrlSearch();
  const q = search.value;

  const pager = usePagedCursor(`esims:${q}`);
  const { data, loading, error } = useAsync(
    () => listEsims({ cursor: pager.cursor, q }),
    [pager.cursor, q],
  );
  const rows = data?.rows ?? [];

  return (
    <div className="space-y-4">
      <PageHeader title="eSIMs" description="Every profile delivered, and the number on it.">
        <SearchBox
          search={search}
          className="w-64"
          placeholder="Email, number, order code or ICCID"
          label="Find an eSIM by email, number, order code or ICCID"
        />
      </PageHeader>
      <PagedList
        columns={7}
        rows={rows}
        loading={loading}
        error={error}
        empty={q ? "No eSIM matches that search." : "No eSIMs have been delivered yet."}
        pager={pager}
        nextCursor={data?.nextCursor}
        head={
          <>
            <TH>ICCID</TH>
            <TH>Number</TH>
            <TH>Order</TH>
            <TH>Profile</TH>
            <TH>Bundle</TH>
            <TH>Bundle expires</TH>
            <TH>Delivered</TH>
          </>
        }
        renderRow={(esim) => (
          <TR key={String(esim.iccid)}>
            <TD>
              <EsimLink iccid={esim.iccid} />
            </TD>
            <TD>
              {esim.msisdn ? (
                <span className="font-mono text-xs">{esim.msisdn}</span>
              ) : (
                <Badge color="amber">not yet assigned</Badge>
              )}
            </TD>
            <TD>{esim.order_code ? <OrderLink code={esim.order_code} /> : "—"}</TD>
            <TD className="text-secondary">{esim.profile_state ?? "—"}</TD>
            <TD className="text-secondary">{esim.last_bundle_state ?? "—"}</TD>
            <TD className="whitespace-nowrap text-muted-foreground">
              {when(esim.bundle_expires_at)}
            </TD>
            <TD className="whitespace-nowrap text-muted-foreground">{when(esim.created_at)}</TD>
          </TR>
        )}
      />
    </div>
  );
}
